import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import CommonDrawerLayout from "../../../../common_components/CommonDrawerLayout";
import { TextInput, Dropdown, FormRow, FormField } from "../../../../common_components/FormInputs";
import { Box, Paper, Tab, Tabs, Typography, Switch, FormControlLabel, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { createProposal, updateProposal, deleteProposal, syncMondayTask, selectMondaySyncLoading, createApprovalActivity, refreshMarketingCalendar } from "../../../../redux/marketingCalendarSlice";
import TakeActionContent from "../../../../common_components/TakeActionContent";
import { selectCanManageProposals, selectCanDraftEmailApproval, selectCanCalendarSync } from "../../../../redux/authSlice";
import { sendDraftEmailReady, sendPromotionCalendarReady } from "../../../../services/emailService";
import ConfirmActionModal from "../../../../common_components/ConfirmActionModal";
import { toast } from "../../../../common_components/Toast/ToastUtils";
import SingleDatePicker from "../../../../common_components/daterangepicker/SingleDatePicker";
import { CalendarDate } from "@internationalized/date";
const tabs = [
    "promotion",
    "themeSummary",
    "productFeatures",
    "landingPage",
    "additionalNotes",
    "description",
];
const tabLabels = {
    promotion: "Promotion",
    themeSummary: "Theme/Summary",
    productFeatures: "Product Features",
    landingPage: "Landing Page",
    additionalNotes: "Additional Notes",
    description: "Description",
};
export default function EventDrawer({ open, onClose, event, title, clientId, selectedMonth, connectionId, user, entityType, forceReadOnly, readOnlyInfo }) {
    const [activeTab, setActiveTab] = useState("promotion");
    const [mainTab, setMainTab] = useState("info");
    const dispatch = useDispatch();
    // ✅ Centralized Permissions
    const canManageProposals = useSelector(selectCanManageProposals);
    const canDraftEmailApproval = useSelector(selectCanDraftEmailApproval);
    const canCalendarSync = useSelector(selectCanCalendarSync);
    const itemType = String(event?.extendedProps?.type || event?.type || "").toLowerCase();
    const isDraft = itemType === "draft" || event?.extendedProps?.isDraft === true;
    const isProposal = itemType === "proposal";
    const labelType = isDraft ? "Draft" : "Proposal";
    const isNewEvent = event?.extendedProps?.isNewEvent === true;
    const isReadOnly = forceReadOnly || readOnlyInfo || ((isProposal || isNewEvent) && !canManageProposals);
    const hideUpdateForm = forceReadOnly || (!canManageProposals && !canDraftEmailApproval);
    // ✅ Monday Sync States
    const [mondayConfirmOpen, setMondayConfirmOpen] = useState(false);
    const [confirmSendOpen, setConfirmSendOpen] = useState(false);
    const [confirmSendLoading, setConfirmSendLoading] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [confirmDeleteLoading, setConfirmDeleteLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const mondaySyncLoading = useSelector(selectMondaySyncLoading);
    const [selectedPendingStatus, setSelectedPendingStatus] = useState("Pending Design");
    // ✅ Helper functions add karo (component ke bahar ya andar)
    const stringToCalendarDate = (dateStr) => {
        if (!dateStr)
            return null;
        const [year, month, day] = dateStr.split('-').map(Number);
        if (!year || !month || !day)
            return null;
        return new CalendarDate(year, month, day);
    };
    const calendarDateToString = (date) => {
        if (!date)
            return '';
        const yyyy = date.year;
        const mm = String(date.month).padStart(2, '0');
        const dd = String(date.day).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };
    const [form, setForm] = useState({
        id: "",
        name: "",
        discountCode: "",
        startDate: "",
        endDate: "",
        whoIsDesigning: "",
        whoIsSending: "",
        clientProvidingAssets: false,
        assetUrl: "",
        mondayTaskId: "", // ✅ Add this
        content: {
            promotion: "",
            themeSummary: "",
            productFeatures: "",
            landingPage: "",
            additionalNotes: "",
            description: "",
        },
    });
    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const payload = {
                name: form.name,
                discountCode: form.discountCode,
                startDate: form.startDate,
                endDate: form.endDate,
                whoIsDesigning: form.whoIsDesigning,
                whoIsSending: form.whoIsSending,
                clientProvidingAssets: form.clientProvidingAssets,
                assetUrl: form.assetUrl,
                monday_task_id: form.mondayTaskId || undefined, // ✅ Add this
                content: form.content
            };
            if (form.id) {
                await dispatch(updateProposal({
                    proposalId: form.id,
                    proposal: payload
                })).unwrap();
            }
            else {
                await dispatch(createProposal({
                    clientId: clientId,
                    connectionId: connectionId,
                    proposal: payload
                })).unwrap();
            }
            await dispatch(refreshMarketingCalendar());
            onClose();
        }
        catch (error) {
            console.error("Proposal save failed", error);
        }
        finally {
            setSubmitting(false);
        }
    };
    // ✅ Status Mapping
    const getValidMondayStatus = (status) => {
        const statusLower = status.toLowerCase();
        if (statusLower === "pending" || statusLower === "pending_dev") {
            return selectedPendingStatus;
        }
        const statusMap = {
            "completed": "Completed",
            "Completed": "Completed",
            "done": "Completed",
            "Done": "Completed",
            "sent_for_approval": "Sent for Approval",
            "Sent for Approval": "Sent for Approval",
            "scheduled": "Scheduled",
            "Scheduled": "Scheduled",
            "pending_copy": "Pending Copy",
            "Pending Copy": "Pending Copy",
            "approved": "Approved",
            "Approved": "Approved",
            "creative_complete": "Creative Complete",
            "Creative Complete": "Creative Complete",
            "cancelled": "Cancelled",
            "Cancelled": "Cancelled",
            "pending_design": "Pending Design",
            "Pending Design": "Pending Design",
            "in_progress": "In Progress",
            "In Progress": "In Progress",
            "not_approved": "Not Approved",
            "Not Approved": "Not Approved",
            "rejected": "Not Approved",
            "Rejected": "Not Approved",
            "pending_client": "Pending Client",
            "Pending Client": "Pending Client",
            "on_hold": "On Hold",
            "On Hold": "On Hold",
        };
        return statusMap[status] || "Pending Dev";
    };
    // ✅ Monday Sync Handler (Updated)
    const handleMondaySync = async () => {
        try {
            const groupName = form.startDate
                ? new Date(form.startDate).toLocaleString("en-US", { month: "long", year: "numeric" })
                : new Date().toLocaleString("en-US", { month: "long", year: "numeric" });
            const validStatus = getValidMondayStatus(event?.extendedProps?.status || "pending");
            // ✅ Call Monday sync API
            const result = await dispatch(syncMondayTask({
                task: form.name,
                sendDate: form.startDate,
                creativeDueDate: form.endDate,
                status: validStatus,
                code: form.discountCode,
                groupName: groupName,
                discountCode: form.discountCode,
                whoIsDesigning: form.whoIsDesigning,
                whoIsSending: form.whoIsSending,
                clientProvidingAssets: form.clientProvidingAssets ? "Yes" : "No",
                dropboxUrl: form.assetUrl,
                promotion: form.content.promotion,
                themeSummary: form.content.themeSummary,
                productFeatures: form.content.productFeatures,
                proposalId: form.id,
                mondayTaskId: form.mondayTaskId, // ✅ Send existing mondayTaskId if available
                hideToast: true // ✅ Suppress slice toast
            })).unwrap();
            // ✅ Get monday_task_id from response
            const newMondayTaskId = result?.data?.monday_task_id || result?.data?.item?.id;
            if (newMondayTaskId) {
                // ✅ Update form state only (DB already updated in backend)
                setForm(prev => ({ ...prev, mondayTaskId: newMondayTaskId }));
                await dispatch(updateProposal({
                    proposalId: form.id,
                    proposal: {
                        ...form,
                        monday_task_id: newMondayTaskId,
                        hideToast: true // ✅ Suppress slice toast
                    }
                })).unwrap();
            }
            // ✅ Refresh calendar data
            await dispatch(refreshMarketingCalendar());
            toast.success("Synced to Monday.com successfully!");
            setMondayConfirmOpen(false);
        }
        catch (error) {
            console.error("Monday sync failed", error);
        }
    };
    const handleTakeActionClick = async (type) => {
        if (type === "monday") {
            setMondayConfirmOpen(true);
        }
        else if (type === "send") {
            setConfirmSendOpen(true);
        }
        else if (type === "trash") {
            setConfirmDeleteOpen(true);
        }
    };
    const handleSendSubmit = async () => {
        setConfirmSendLoading(true);
        try {
            // ✅ 1. Approval Activity
            await dispatch(createApprovalActivity({
                send_by: entityType === "client" ? "client" : "user",
                user_id: user?.id,
                status: "pending",
                campaign_id: form.id,
                client_id: clientId,
                connection_id: connectionId,
                module: "marketing_calendar",
                hideToast: true
            })).unwrap();
            // ✅ 2. Email Trigger (Fire and forget)
            const sendForApprovalPayload = {
                clientId: clientId,
                actionBy: entityType === "client" ? "client" : "user",
                storeName: form.name,
                month: (event?.extendedProps?.startDate || event?.extendedProps?.send_time || "").split("-")?.[1] || "",
                year: (event?.extendedProps?.startDate || event?.extendedProps?.send_time || "").split("-")?.[0] || "",
                campaignId: form.id,
                // 🔥 explicitly set to pending when sending for approval
                approvalStatus: "pending",
                comment: ""
            };
            if (isDraft) {
                sendDraftEmailReady(sendForApprovalPayload);
            }
            else {
                // Both fire together — same key admin can configure with
                // different recipients (client-facing "ready" vs internal-team
                // "loaded"), same trigger condition as the draft's send flow.
                sendPromotionCalendarReady(sendForApprovalPayload);
            }
            toast.success("Sent for approval successfully!");
            setConfirmSendLoading(false);
            setConfirmSendOpen(false);
            onClose();
            dispatch(refreshMarketingCalendar());
        }
        catch (err) {
            console.error("Send failed", err);
            toast.error("Failed to send for approval. Please try again.");
            setConfirmSendLoading(false);
            setConfirmSendOpen(false);
        }
    };
    const handleDeleteConfirm = async () => {
        if (!form.id)
            return;
        setConfirmDeleteLoading(true);
        try {
            await dispatch(deleteProposal({ proposalId: form.id })).unwrap();
            setConfirmDeleteLoading(false);
            setConfirmDeleteOpen(true); // Close happens in extraReducers or here
            setConfirmDeleteOpen(false);
            onClose();
            dispatch(refreshMarketingCalendar());
        }
        catch (err) {
            console.error("Delete failed", err);
            setConfirmDeleteLoading(false);
        }
    };
    const updateContent = (value) => {
        setForm((prev) => ({
            ...prev,
            content: {
                ...prev.content,
                [activeTab]: value,
            },
        }));
    };
    const quillModules = useMemo(() => ({
        toolbar: [
            [{ header: [false, 1, 2, 3] }],
            [{ list: "ordered" }, { list: "bullet" }],
            ["bold", "italic", "underline"],
            ["link"],
            ["blockquote"],
            ["clean"],
        ],
    }), []);
    const quillFormats = [
        "header",
        "list",
        "bullet",
        "bold",
        "italic",
        "underline",
        "link",
        "blockquote",
    ];
    useEffect(() => {
        if (!open)
            return;
        const p = event?.extendedProps || {};
        if (p.isNewEvent) {
            const d = p.selectedDate || "";
            setForm(prev => ({
                ...prev,
                name: "",
                id: "",
                discountCode: "",
                startDate: d,
                endDate: d,
                whoIsDesigning: "",
                whoIsSending: "",
                clientProvidingAssets: false,
                assetUrl: "",
                mondayTaskId: "", // ✅ Reset
                content: {
                    promotion: "",
                    themeSummary: "",
                    productFeatures: "",
                    landingPage: "",
                    additionalNotes: "",
                    description: "",
                }
            }));
            setActiveTab("promotion");
            setMainTab("info");
            return;
        }
        if (!p.isNewEvent) {
            const eventName = p.name || event?.title || p.campaign_name || "";
            setForm(prev => ({
                ...prev,
                name: eventName,
                id: p.id || event?.id || p._id || "",
                discountCode: p.discountCode || "",
                startDate: (p.startDate || "").slice(0, 10),
                endDate: (p.endDate || p.startDate || "").slice(0, 10),
                whoIsDesigning: p.whoIsDesigning || "",
                whoIsSending: p.whoIsSending || "",
                clientProvidingAssets: !!p.clientProvidingAssets,
                assetUrl: p.assetUrl || "",
                mondayTaskId: p.mondayTaskId || "", // ✅ Get from event
                content: {
                    promotion: p.content?.promotion || "",
                    themeSummary: p.content?.themeSummary || "",
                    productFeatures: p.content?.productFeatures || "",
                    landingPage: p.content?.landingPage || "",
                    additionalNotes: p.content?.additionalNotes || "",
                    description: p.content?.description || "",
                }
            }));
            setActiveTab("promotion");
            setMainTab("info");
        }
    }, [open, event]);
    return (_jsxs(_Fragment, { children: [_jsxs(CommonDrawerLayout, { open: open, onClose: onClose, title: form.id ? `${labelType} Details / ${form.name}` : `Add a New ${labelType}`, width: "md", headerActions: mainTab === "info" ? (!forceReadOnly && canManageProposals ? (_jsxs("button", { onClick: handleSubmit, disabled: submitting, style: {
                        background: "#FF008E",
                        color: "#fff",
                        border: "none",
                        padding: "8px 18px",
                        borderRadius: "6px",
                        cursor: submitting ? "not-allowed" : "pointer",
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        opacity: submitting ? 0.7 : 1
                    }, children: [submitting ? (_jsx(CircularProgress, { size: 16, sx: { color: "#fff" } })) : (_jsx("img", { src: "/assets/Icon.svg", alt: "Save Icon", width: 16, height: 16 })), submitting ? "Saving..." : "Save"] })) : null) : mainTab === "takeAction" ? (!forceReadOnly && !readOnlyInfo && (_jsxs(Box, { sx: { display: "flex", gap: "12px", alignItems: "center" }, children: [canCalendarSync && event?.extendedProps?.type !== "draft" && (_jsxs(Box, { onClick: () => handleTakeActionClick("monday"), sx: {
                                width: 40,
                                height: 40,
                                borderRadius: "8px",
                                border: form.mondayTaskId ? "2px solid #00CA72" : "1px solid #E5E7EB", // ✅ Green border if synced
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                background: form.mondayTaskId ? "#E6FFF5" : "#fff", // ✅ Light green bg if synced
                                transition: "all 0.2s",
                                position: "relative",
                                "&:hover": { borderColor: "#D1D5DB", background: "#F9FAFB" }
                            }, children: [_jsx("img", { src: "/assets/monday.svg", alt: "Monday", style: { width: 20, height: 20 } }), form.mondayTaskId && (_jsx(Box, { sx: {
                                        position: "absolute",
                                        top: -4,
                                        right: -4,
                                        width: 14,
                                        height: 14,
                                        borderRadius: "50%",
                                        backgroundColor: "#00CA72",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }, children: _jsx(Typography, { sx: { color: "#fff", fontSize: "10px", fontWeight: "bold" }, children: "\u2713" }) }))] })), event?.extendedProps?.visible_to_client == false && canDraftEmailApproval && (_jsx(Box, { onClick: () => handleTakeActionClick("send"), sx: {
                                width: 40,
                                height: 40,
                                borderRadius: "8px",
                                border: "1px solid #E5E7EB",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                background: "#fff",
                                transition: "all 0.2s",
                                "&:hover": { borderColor: "#D1D5DB", background: "#F9FAFB" }
                            }, children: _jsx("img", { src: "/assets/head_send.svg", alt: "Send", style: { width: 20, height: 20 } }) })), canManageProposals && (_jsx(Box, { onClick: () => handleTakeActionClick("trash"), sx: {
                                width: 40,
                                height: 40,
                                borderRadius: "8px",
                                border: "2px solid #FCA5A5",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                background: "#FEF2F2",
                                transition: "all 0.2s",
                                "&:hover": { borderColor: "#F87171", background: "#FEE2E2" }
                            }, children: _jsx("img", { src: "/assets/head_trash.svg", alt: "Trash", style: { width: 20, height: 20 } }) }))] }))) : undefined, children: [_jsx(Box, { sx: { px: 1, pt: 1 }, children: form.id && (_jsxs(Box, { sx: {
                                display: "flex",
                                backgroundColor: "#F9FAFB",
                                borderRadius: "8px",
                                p: "4px",
                                border: "1px solid #E5E7EB",
                                width: "100%",
                            }, children: [_jsx(Box, { onClick: () => setMainTab("info"), sx: {
                                        flex: 1,
                                        textAlign: "center",
                                        py: 1,
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        fontWeight: 500,
                                        fontSize: "14px",
                                        transition: "all 0.3s ease",
                                        color: mainTab === "info" ? "#ffffff" : "#6B7280",
                                        backgroundColor: mainTab === "info" ? "#FF008E" : "#F4F4F5",
                                    }, children: "Information" }), _jsx(Box, { onClick: () => setMainTab("takeAction"), sx: {
                                        flex: 1,
                                        textAlign: "center",
                                        py: 1,
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        fontWeight: 500,
                                        fontSize: "14px",
                                        transition: "all 0.3s ease",
                                        color: mainTab === "takeAction" ? "#ffffff" : "#6B7280",
                                        backgroundColor: mainTab === "takeAction" ? "#FF008E" : "#F4F4F5",
                                    }, children: "Take Action" })] })) }), mainTab === "info" && (_jsxs("div", { style: { padding: 8 }, children: [_jsxs("div", { style: {
                                    border: "1px solid #E5E7EB",
                                    borderRadius: 8,
                                    padding: 8,
                                    marginBottom: 8,
                                    backgroundColor: "#fff",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 8
                                }, children: [_jsxs("h3", { className: "all-Font-Sizes", style: { color: "#FF008E", margin: 0, borderBottom: "1px solid #EAECF0", paddingBottom: 8 }, children: [labelType, " Information"] }), _jsx(FormField, { children: _jsx(TextInput, { label: "Name", required: true, disabled: isReadOnly, placeholder: `Enter ${labelType} Name`, value: form.name, onChange: (e) => setForm((p) => ({ ...p, name: e.target.value })) }) }), _jsx(FormField, { children: _jsx(TextInput, { label: "Discount Code", disabled: isReadOnly, placeholder: "Enter Discount Code", value: form.discountCode, onChange: (e) => setForm((p) => ({ ...p, discountCode: e.target.value })) }) }), _jsxs(FormRow, { columns: 2, children: [_jsx(SingleDatePicker, { label: "Start Date", required: true, value: stringToCalendarDate(form.startDate), onChange: (date) => setForm((p) => ({
                                                    ...p,
                                                    startDate: calendarDateToString(date),
                                                })), placeholder: "Select Start Date", disabled: isReadOnly }), _jsx(SingleDatePicker, { label: "End Date", value: stringToCalendarDate(form.endDate), onChange: (date) => setForm((p) => ({
                                                    ...p,
                                                    endDate: calendarDateToString(date),
                                                })), placeholder: "Select End Date", disabled: isReadOnly })] }), _jsxs(FormRow, { columns: 2, children: [_jsx(Dropdown, { label: "Who Is Designing", disabled: isReadOnly, value: form.whoIsDesigning, options: [
                                                    { value: "G8A", label: "G8A" },
                                                    { value: "Client", label: "Client" },
                                                ], onChange: (v) => setForm((p) => ({ ...p, whoIsDesigning: v })) }), _jsx(Dropdown, { label: "Who Is Sending", disabled: isReadOnly, value: form.whoIsSending, options: [
                                                    { value: "G8A", label: "G8A" },
                                                    { value: "Client", label: "Client" },
                                                ], onChange: (v) => setForm((p) => ({ ...p, whoIsSending: v })) })] }), _jsxs("div", { className: "compare-toggle-container normal-switch", style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: 0, marginLeft: 0, padding: 0 }, children: [_jsx("p", { className: "all-Font-Sizes", style: { margin: 0 }, children: "Client providing assets?" }), _jsx(FormControlLabel, { className: "compare-label", control: _jsx(Switch, { className: "compare-switch", size: "medium", disabled: isReadOnly, checked: form.clientProvidingAssets, onChange: (e) => setForm((p) => ({
                                                        ...p,
                                                        clientProvidingAssets: e.target.checked,
                                                    })), sx: {
                                                        "& .MuiSwitch-switchBase.Mui-checked": {
                                                            color: "#ff008e",
                                                        },
                                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                                            backgroundColor: "#ff008e",
                                                        },
                                                    } }), label: "Yes", sx: { m: 0, '& .MuiFormControlLabel-label': { fontSize: '14px', color: '#667085' } } })] }), form.clientProvidingAssets && (_jsx(FormField, { children: _jsx(TextInput, { disabled: isReadOnly, placeholder: "Provide asset URL (e.g., Dropbox or Google Drive URL)", value: form.assetUrl, onChange: (e) => setForm((p) => ({ ...p, assetUrl: e.target.value })) }) }))] }), _jsxs(Paper, { variant: "outlined", sx: {
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    borderColor: "#EAECF0",
                                    background: "#fff",
                                }, children: [_jsx(Tabs, { value: activeTab, onChange: (_, v) => setActiveTab(v), variant: "scrollable", scrollButtons: false, sx: {
                                            borderBottom: "1px solid #EAECF0",
                                            "& .MuiTabs-indicator": {
                                                backgroundColor: "#FF008E",
                                                height: 2,
                                            },
                                        }, children: tabs.map((t, idx) => (_jsx(Tab, { value: t, label: tabLabels[t], className: "all-Font-Sizes", sx: {
                                                textTransform: "none",
                                                fontWeight: 500,
                                                color: "#667085",
                                                minHeight: 48,
                                                borderRight: idx !== tabs.length - 1 ? "1px solid #EAECF0" : "none",
                                                "&.Mui-selected": {
                                                    color: "#FF008E",
                                                    fontWeight: 500,
                                                    backgroundColor: "#FFFAFD",
                                                },
                                            } }, t))) }), _jsx(Box, { sx: { p: 2 }, children: _jsx(ReactQuill, { theme: "snow", className: "my-quill proposal-quill", readOnly: isReadOnly, modules: quillModules, formats: quillFormats, value: form.content[activeTab], onChange: updateContent, placeholder: "Type your text here..." }, activeTab) })] })] })), mainTab === "takeAction" && form.id && (_jsx(Box, { sx: { p: 1 }, children: _jsx(TakeActionContent, { client_id: clientId || "", connection_id: connectionId || "", campaign_id: form.id || event?.extendedProps?._id || event?.id || "", currentStatus: event?.extendedProps?.status, marketingcalenderRefresh: true, is_upload_show: true, hideUpdateForm: hideUpdateForm, data: { ...form, type: itemType, visible_to_client: event?.extendedProps?.visible_to_client } }) }))] }), _jsx(ConfirmActionModal, { open: mondayConfirmOpen, loading: mondaySyncLoading, onClose: () => setMondayConfirmOpen(false), onConfirm: handleMondaySync, title: form.mondayTaskId ? "Update in Monday.com" : "Sync to Monday.com", description: form.mondayTaskId
                    ? `This ${labelType.toLowerCase()} (${form.name}) is already synced. Do you want to update the details in Monday.com?`
                    : `Are you sure you want to sync this ${labelType.toLowerCase()} (${form.name}) to Monday.com?`, confirmText: form.mondayTaskId ? "Update" : "Sync Now", isDeleteIcon: false, buttonColor: "#FF008E", buttonHoverColor: "#E6007A", children: (() => {
                    const st = (event?.extendedProps?.status || "pending").toLowerCase();
                    if (st === "pending" || st === "pending_dev") {
                        return (_jsxs(Box, { sx: { mt: 2, textAlign: "left" }, children: [_jsxs(Typography, { sx: { mb: 1, fontSize: 14, fontWeight: 500, color: "#344054" }, children: ["Select Initial Status for Pending ", labelType, ":"] }), _jsx(Dropdown, { value: selectedPendingStatus, onChange: (val) => setSelectedPendingStatus(val), options: [
                                        { value: "Pending Design", label: "Pending Design" },
                                        { value: "Pending Client", label: "Pending Client" }
                                    ], usePortal: true })] }));
                    }
                    return null;
                })() }), _jsx(ConfirmActionModal, { open: confirmSendOpen, loading: confirmSendLoading, onClose: () => setConfirmSendOpen(false), onConfirm: handleSendSubmit, title: "Send for Approval", description: "Are you sure you want to send this for approval?", confirmText: "Confirm", isDeleteIcon: false, buttonColor: "#FF008E", buttonHoverColor: "#E6007A" }), _jsx(ConfirmActionModal, { open: confirmDeleteOpen, loading: confirmDeleteLoading, onClose: () => setConfirmDeleteOpen(false), onConfirm: handleDeleteConfirm, title: `Delete ${labelType}`, description: `Are you sure you want to delete this ${labelType.toLowerCase()}? This action cannot be undone and will permanently remove all history, chats, and associated data.`, confirmText: "Delete", isDeleteIcon: true, buttonColor: "#DC2626", buttonHoverColor: "#B91C1C" })] }));
}
